import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  GET_EXIT_STRATEGY_TEMPLATES, 
  GET_INVALIDATION_STRATEGY_TEMPLATES 
} from '../graphql/queries/strategyTemplates';
import {
  DELETE_EXIT_STRATEGY_TEMPLATE,
  DELETE_INVALIDATION_STRATEGY_TEMPLATE
} from '../graphql/mutations/strategyTemplates';
import ExitStrategyTemplateModal from '../components/ExitStrategyTemplateModal';
import InvalidationStrategyTemplateModal from '../components/InvalidationStrategyTemplateModal';
import BulkStrategyAssignment from '../components/BulkStrategyAssignment';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import toast from 'react-hot-toast';
import { formatDate } from '../utils/dateUtils';
import EmptyState from '../components/EmptyState';

const StrategiesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get initial tab from URL parameter
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'invalidation' || tab === 'exit' || tab === 'apply') {
      return tab;
    }
    return 'exit';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  
  // Update tab when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'invalidation' || tab === 'exit' || tab === 'apply') {
      setActiveTab(tab);
    }
  }, [location.search]);
  
  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/strategies?tab=${tab}`, { replace: true });
  };
  const [showExitModal, setShowExitModal] = useState(false);
  const [showInvalidationModal, setShowInvalidationModal] = useState(false);
  const [editingExitTemplate, setEditingExitTemplate] = useState(null);
  const [editingInvalidationTemplate, setEditingInvalidationTemplate] = useState(null);
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'exit' | 'invalidation', id, name }

  // Queries
  const { data: exitData, loading: exitLoading, refetch: refetchExit } = useQuery(GET_EXIT_STRATEGY_TEMPLATES);
  const { data: invalidationData, loading: invalidationLoading, refetch: refetchInvalidation } = useQuery(GET_INVALIDATION_STRATEGY_TEMPLATES);

  // Mutations
  const [deleteExitTemplate] = useMutation(DELETE_EXIT_STRATEGY_TEMPLATE);
  const [deleteInvalidationTemplate] = useMutation(DELETE_INVALIDATION_STRATEGY_TEMPLATE);

  const exitTemplates = exitData?.exitStrategyTemplates || [];
  const invalidationTemplates = invalidationData?.invalidationStrategyTemplates || [];

  const handleDeleteExitTemplate = (id, name) => {
    setDeleteConfirm({ type: 'exit', id, name });
  };

  const handleDeleteInvalidationTemplate = (id, name) => {
    setDeleteConfirm({ type: 'invalidation', id, name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const { type, id } = deleteConfirm;
    
    try {
      if (type === 'exit') {
        const { data } = await deleteExitTemplate({ variables: { id } });
        if (data?.deleteExitStrategyTemplate?.success) {
          toast.success('Template deleted');
          refetchExit();
        } else {
          toast.error(data?.deleteExitStrategyTemplate?.error || 'Failed to delete');
        }
      } else {
        const { data } = await deleteInvalidationTemplate({ variables: { id } });
        if (data?.deleteInvalidationStrategyTemplate?.success) {
          toast.success('Template deleted');
          refetchInvalidation();
        } else {
          toast.error(data?.deleteInvalidationStrategyTemplate?.error || 'Failed to delete');
        }
      }
    } catch (err) {
      toast.error('Failed to delete template');
    }
    
    setDeleteConfirm(null);
  };

  const handleEditExit = (template) => {
    setEditingExitTemplate(template);
    setShowExitModal(true);
  };

  const handleEditInvalidation = (template) => {
    setEditingInvalidationTemplate(template);
    setShowInvalidationModal(true);
  };

  const handleCloseExitModal = () => {
    setShowExitModal(false);
    setEditingExitTemplate(null);
    refetchExit();
  };

  const handleCloseInvalidationModal = () => {
    setShowInvalidationModal(false);
    setEditingInvalidationTemplate(null);
    refetchInvalidation();
  };

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Strategy Templates</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('exit')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'exit'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Exit Strategies
          </button>
          <button
            onClick={() => handleTabChange('invalidation')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invalidation'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Invalidation Strategies
          </button>
          {(exitTemplates.length > 0 || invalidationTemplates.length > 0) && (
            <button
              onClick={() => handleTabChange('apply')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'apply'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Apply to Positions
            </button>
          )}
        </nav>
      </div>

      {/* Exit Strategies Tab */}
      {activeTab === 'exit' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-gray-600 dark:text-gray-400">
              Create reusable exit strategy templates to define when to take profits.
            </p>
            {exitTemplates.length > 0 && (
              <button
                onClick={() => setShowExitModal(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
              >
                + New Exit Strategy
              </button>
            )}
          </div>

          {exitLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : exitTemplates.length === 0 ? (
            <EmptyState
              title="No exit strategy templates yet"
              description="Create reusable exit strategy templates to define when to take profits."
              action={() => setShowExitModal(true)}
              actionLabel="Create your first strategy"
            />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="divide-y divide-gray-200 dark:divide-gray-700 md:hidden">
                {exitTemplates.map((template) => (
                  <div key={template.id} className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => handleEditExit(template)}
                        className="min-w-0 text-left"
                      >
                        <div className="truncate text-sm font-medium text-blue-600 dark:text-blue-400">
                          {template.name}
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Updated {formatDate(template.updatedAt)} · {template.positionsCount} positions
                        </div>
                      </button>
                      <div className="shrink-0 text-right text-sm text-gray-700 dark:text-gray-300">
                        <div>{template.targetMultiple}x</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {template.exitLevelsCount} @ {template.exitLevelsSpread}%
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {template.takeInitialsEnabled && (
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded">
                          TI {template.takeInitialsMultiple}x
                        </span>
                      )}
                      {template.hodlEnabled && (
                        <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs rounded">
                          HODL {template.hodlPercentage}%
                        </span>
                      )}
                      {template.advancedDistributionEnabled && (
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                          Advanced
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <button
                        type="button"
                        onClick={() => handleEditExit(template)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteExitTemplate(template.id, template.name)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <table className="hidden min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:table">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Target</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Levels</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Options</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Used By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Updated</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {exitTemplates.map((template) => (
                    <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {template.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {template.targetMultiple}x
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {template.exitLevelsCount} @ {template.exitLevelsSpread}% spread
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        <div className="flex gap-2">
                          {template.takeInitialsEnabled && (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded">
                              TI {template.takeInitialsMultiple}x
                            </span>
                          )}
                          {template.hodlEnabled && (
                            <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs rounded">
                              HODL {template.hodlPercentage}%
                            </span>
                          )}
                          {template.advancedDistributionEnabled && (
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                              Advanced
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {template.positionsCount} positions
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(template.updatedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => handleEditExit(template)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteExitTemplate(template.id, template.name)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invalidation Strategies Tab */}
      {activeTab === 'invalidation' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-gray-600 dark:text-gray-400">
              Create reusable invalidation templates to define when to reconsider positions.
            </p>
            {invalidationTemplates.length > 0 && (
              <button
                onClick={() => setShowInvalidationModal(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
              >
                + New Invalidation Strategy
              </button>
            )}
          </div>

          {invalidationLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : invalidationTemplates.length === 0 ? (
            <EmptyState
              title="No invalidation strategy templates yet"
              description="Create reusable invalidation templates to define when to reconsider positions."
              action={() => setShowInvalidationModal(true)}
              actionLabel="Create your first strategy"
            />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="divide-y divide-gray-200 dark:divide-gray-700 md:hidden">
                {invalidationTemplates.map((template) => (
                  <div key={template.id} className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => handleEditInvalidation(template)}
                        className="min-w-0 text-left"
                      >
                        <div className="truncate text-sm font-medium text-blue-600 dark:text-blue-400">
                          {template.name}
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Updated {formatDate(template.updatedAt)} · {template.positionsCount} positions
                        </div>
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {template.priceBasedEnabled && (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs rounded">
                          Price drop &gt;{template.priceDropPercentage}%
                        </span>
                      )}
                      {template.timeBasedEnabled && (
                        <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 text-xs rounded">
                          &gt;{template.timeDurationDays} days
                        </span>
                      )}
                      {template.priceBasedEnabled && template.timeBasedEnabled && (
                        <span className="text-xs text-gray-400">OR</span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <button
                        type="button"
                        onClick={() => handleEditInvalidation(template)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteInvalidationTemplate(template.id, template.name)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <table className="hidden min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:table">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Conditions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Used By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Updated</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {invalidationTemplates.map((template) => (
                    <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {template.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        <div className="flex gap-2">
                          {template.priceBasedEnabled && (
                            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs rounded">
                              Price drop &gt;{template.priceDropPercentage}%
                            </span>
                          )}
                          {template.timeBasedEnabled && (
                            <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 text-xs rounded">
                              &gt;{template.timeDurationDays} days
                            </span>
                          )}
                          {template.priceBasedEnabled && template.timeBasedEnabled && (
                            <span className="text-xs text-gray-400">(OR)</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {template.positionsCount} positions
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(template.updatedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => handleEditInvalidation(template)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteInvalidationTemplate(template.id, template.name)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Apply to Positions Tab */}
      {activeTab === 'apply' && (
        <BulkStrategyAssignment />
      )}

      {/* Modals */}
      {showExitModal && (
        <ExitStrategyTemplateModal
          template={editingExitTemplate}
          onClose={handleCloseExitModal}
        />
      )}
      {showInvalidationModal && (
        <InvalidationStrategyTemplateModal
          template={editingInvalidationTemplate}
          onClose={handleCloseInvalidationModal}
        />
      )}
      {deleteConfirm && (
        <ConfirmDeleteModal
          title="Delete Template"
          message={`Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default StrategiesPage;
